import { useEffect, useState } from 'react';
import { TrendingUp, Wallet, CalendarCheck, Inbox, Briefcase, Percent, BarChart3 } from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend, Cell,
} from 'recharts';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { adminGetAnalytics } from '@/lib/api';
import { formatCents } from '@/lib/money';
import { ADMIN_ANALYTICS } from '@/constants/testIds';
import { StatusBadge } from '@/components/admin/AdminBookingsTab';
import { SkeletonList } from '@/components/SkeletonList';

const GOLD = 'hsl(40 49% 57%)';
const GOLD_DIM = 'hsla(40, 49%, 57%, 0.35)';
const MUTED = 'hsl(30 8% 60%)';
const GRID = 'rgba(255,255,255,0.06)';
const SERVICE_COLORS = [GOLD, 'hsl(200 60% 60%)', 'hsl(265 50% 68%)', 'hsl(152 46% 55%)', 'hsl(8 55% 62%)', 'hsl(40 30% 45%)'];

const monthLabel = (key) => {
  const [y, m] = key.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('en-US', { month: 'short' });
};

const ChartTooltip = ({ active, payload, label, money = true }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-[hsl(var(--lxra-border))] bg-[#141416]/95 backdrop-blur px-3 py-2 text-xs shadow-xl">
      <p className="text-[hsl(var(--lxra-muted))] mb-1">{label}</p>
      {payload.map((p) => (
        <p key={p.dataKey} className="text-[hsl(var(--lxra-fg))]">
          <span className="inline-block h-2 w-2 rounded-sm mr-1.5" style={{ background: p.color || p.fill }} />
          {p.name}: <strong>{money ? formatCents(p.value) : p.value}</strong>
        </p>
      ))}
    </div>
  );
};

const KPI = ({ testKey, icon: Icon, label, value, hint }) => (
  <div data-testid={ADMIN_ANALYTICS.kpi(testKey)} className="rounded-2xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] p-5">
    <div className="flex items-center justify-between">
      <span className="flex h-9 w-9 items-center justify-center rounded-full border border-[hsl(var(--lxra-gold))]/30 text-[hsl(var(--lxra-gold))]"><Icon className="h-4 w-4" /></span>
      <span className="font-display text-2xl text-[hsl(var(--lxra-fg))]">{value}</span>
    </div>
    <p className="mt-3 text-[11px] uppercase tracking-[0.12em] text-[hsl(var(--lxra-muted))]">{label}</p>
    {hint && <p className="mt-0.5 text-[11px] text-[hsl(var(--lxra-muted))]">{hint}</p>}
  </div>
);

export const AdminAnalyticsTab = () => {
  const [months, setMonths] = useState('6');
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    adminGetAnalytics(Number(months)).then(setData).catch(() => setData(false));
  }, [months]);

  if (data === false) {
    return <p className="text-sm text-[hsl(var(--lxra-muted))]">Could not load analytics. Please refresh.</p>;
  }

  const k = data?.kpis;
  const revenueSeries = (data?.revenue_by_month || []).map((r) => ({ ...r, label: monthLabel(r.month) }));
  const hasAnyBooking = data && data.by_status.some((s) => s.count > 0);

  return (
    <div className="space-y-6" data-testid={ADMIN_ANALYTICS.section}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-[hsl(var(--lxra-gold))]">LXRA Creator</p>
          <h2 className="font-display text-2xl tracking-[0.03em] text-[hsl(var(--lxra-fg))] mt-1">Revenue &amp; Analytics</h2>
          <p className="text-xs text-[hsl(var(--lxra-muted))] mt-1">Revenue counts payments you&apos;ve recorded; booked value is the total price of non-cancelled bookings by session month.</p>
        </div>
        <Select value={months} onValueChange={setMonths}>
          <SelectTrigger data-testid={ADMIN_ANALYTICS.rangeSelect} className="h-9 w-40 bg-[#141416] border-[hsl(var(--lxra-border))] text-xs text-[hsl(var(--lxra-fg))]"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#141416] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))]">
            <SelectItem value="3">Last 3 months</SelectItem>
            <SelectItem value="6">Last 6 months</SelectItem>
            <SelectItem value="12">Last 12 months</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {!data ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <SkeletonList count={6} className="h-28 rounded-2xl bg-[#141416]" />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <KPI testKey="revenue_month" icon={TrendingUp} label="Revenue this month" value={formatCents(k.revenue_this_month_cents)} hint={`${formatCents(k.revenue_ytd_cents)} year to date`} />
            <KPI testKey="outstanding" icon={Wallet} label="Outstanding balance" value={formatCents(k.outstanding_balance_cents)} hint="Across all active bookings" />
            <KPI testKey="upcoming" icon={CalendarCheck} label="Upcoming sessions" value={k.upcoming_sessions} hint={`Booked in the next ${k.upcoming_window_days} days`} />
            <KPI testKey="requested" icon={Inbox} label="Requests to confirm" value={k.requested_bookings} hint="Submitted from the booking page" />
            <KPI testKey="avg_value" icon={Briefcase} label="Average booking value" value={formatCents(k.average_booking_value_cents)} hint={`${k.active_bookings} active booking${k.active_bookings === 1 ? '' : 's'}`} />
            <KPI testKey="conversion" icon={Percent} label="Lead conversion" value={`${k.lead_conversion_rate}%`} hint={`${data.lead_funnel.booked} of ${data.lead_funnel.total} leads marked booked`} />
          </div>

          {!hasAnyBooking && (
            <div data-testid={ADMIN_ANALYTICS.empty} className="rounded-2xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] p-10 text-center">
              <BarChart3 className="h-7 w-7 mx-auto text-[hsl(var(--lxra-gold))]" />
              <p className="mt-3 text-sm text-[hsl(var(--lxra-fg))]">No bookings yet — charts will fill in as you add them.</p>
              <p className="mt-1 text-xs text-[hsl(var(--lxra-muted))]">Add a booking in the Bookings tab, or share /book with a client.</p>
            </div>
          )}

          <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
            <div className="xl:col-span-3 rounded-2xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] p-5" data-testid={ADMIN_ANALYTICS.revenueChart}>
              <p className="text-[11px] uppercase tracking-[0.12em] text-[hsl(var(--lxra-muted))] mb-4">Revenue vs. booked value by month</p>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={revenueSeries} barGap={4} margin={{ top: 4, right: 4, left: -12, bottom: 0 }}>
                    <CartesianGrid vertical={false} stroke={GRID} />
                    <XAxis dataKey="label" tick={{ fill: MUTED, fontSize: 11 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fill: MUTED, fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={(v) => formatCents(v, { compact: true })} />
                    <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(255,255,255,0.03)' }} />
                    <Legend wrapperStyle={{ fontSize: 11, color: MUTED }} iconType="square" iconSize={8} />
                    <Bar dataKey="revenue_cents" name="Revenue received" fill={GOLD} radius={[4, 4, 0, 0]} />
                    <Bar dataKey="booked_value_cents" name="Booked value" fill={GOLD_DIM} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="xl:col-span-2 rounded-2xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] p-5" data-testid={ADMIN_ANALYTICS.serviceChart}>
              <p className="text-[11px] uppercase tracking-[0.12em] text-[hsl(var(--lxra-muted))] mb-4">Booked value by service</p>
              {data.by_service.length === 0 ? (
                <p className="text-xs text-[hsl(var(--lxra-muted))] py-10 text-center">No bookings to break down yet.</p>
              ) : (
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.by_service} layout="vertical" margin={{ top: 4, right: 12, left: 8, bottom: 0 }}>
                      <CartesianGrid horizontal={false} stroke={GRID} />
                      <XAxis type="number" tick={{ fill: MUTED, fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={(v) => formatCents(v, { compact: true })} />
                      <YAxis type="category" dataKey="label" width={96} tick={{ fill: MUTED, fontSize: 11 }} axisLine={false} tickLine={false} />
                      <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(255,255,255,0.03)' }} />
                      <Bar dataKey="value_cents" name="Booked value" radius={[0, 4, 4, 0]}>
                        {data.by_service.map((row, i) => <Cell key={row.label ?? row.service_slug} fill={SERVICE_COLORS[i % SERVICE_COLORS.length]} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="rounded-2xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] p-5" data-testid={ADMIN_ANALYTICS.statusList}>
              <p className="text-[11px] uppercase tracking-[0.12em] text-[hsl(var(--lxra-muted))] mb-4">Pipeline by status</p>
              <div className="space-y-2.5">
                {data.by_status.map((s) => {
                  const total = data.by_status.reduce((n, x) => n + x.count, 0) || 1;
                  return (
                    <div key={s.status} className="flex items-center gap-3">
                      <div className="w-24 shrink-0"><StatusBadge status={s.status} /></div>
                      <div className="flex-1 h-1.5 rounded-full bg-[#141416] overflow-hidden">
                        <div className="h-full bg-[hsl(var(--lxra-gold))]/70" style={{ width: `${(s.count / total) * 100}%` }} />
                      </div>
                      <span className="w-6 text-right text-xs text-[hsl(var(--lxra-fg))]">{s.count}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="rounded-2xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] p-5" data-testid={ADMIN_ANALYTICS.leadFunnel}>
              <p className="text-[11px] uppercase tracking-[0.12em] text-[hsl(var(--lxra-muted))] mb-4">Lead funnel</p>
              <div className="grid grid-cols-4 gap-2 text-center">
                {[
                  ['Total', data.lead_funnel.total],
                  ['New', data.lead_funnel.new],
                  ['Contacted', data.lead_funnel.contacted],
                  ['Booked', data.lead_funnel.booked],
                ].map(([label, n]) => (
                  <div key={label} className="rounded-xl border border-[hsl(var(--lxra-border))] bg-[#141416] py-4">
                    <p className="font-display text-2xl text-[hsl(var(--lxra-fg))]">{n}</p>
                    <p className="text-[10px] uppercase tracking-[0.1em] text-[hsl(var(--lxra-muted))] mt-1">{label}</p>
                  </div>
                ))}
              </div>
              <p className="mt-3 text-[11px] text-[hsl(var(--lxra-muted))]">
                Leads come from the Session Planner and contact form. Mark a lead <span className="text-[hsl(var(--lxra-fg))]">booked</span> in the Leads tab to count it here.
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
